import { useEffect, useState } from "react";
import { NavLink, Routes, Route, Navigate } from "react-router-dom";
import { adminListChannels, adminListUsers, adminCreateContent, adminDeleteContent, adminAddProgram, adminRemoveProgram, adminDeleteChannel, adminCreateChannel, adminUpdateChannel, fetchLibrary, adminListAds, adminApproveAd, adminRejectAd, adminDeleteAd, adminListNews, adminCreateNews, adminDeleteNews, adminRefreshRss, api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Check, X, RefreshCw, Edit, Users as UsersIcon, DollarSign, Megaphone, Radio, Newspaper, Gift, TrendingUp, MousePointerClick, Eye } from "lucide-react";

export default function Admin() {
  return (
    <div className="pt-16" data-testid="admin-page">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        <p className="text-[#D4AF37] text-[11px] font-semibold tracking-[0.25em] uppercase mb-2">Control Room</p>
        <h1 className="font-heading text-4xl font-bold mb-6">Admin Panel</h1>
        <div className="flex gap-2 mb-8 flex-wrap">
          <Tab to="/admin/dashboard" label="Dashboard" />
          <Tab to="/admin/channels" label="Channels & Schedule" />
          <Tab to="/admin/library" label="Library" />
          <Tab to="/admin/ads" label="Ad Approvals" />
          <Tab to="/admin/news" label="News" />
          <Tab to="/admin/users" label="Users" />
        </div>
        <Routes>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="channels" element={<Channels />} />
          <Route path="library" element={<Library />} />
          <Route path="ads" element={<Ads />} />
          <Route path="news" element={<News />} />
          <Route path="users" element={<Users />} />
        </Routes>
      </div>
    </div>
  );
}

function Dashboard() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/metrics").then(r => setM(r.data)); }, []);
  if (!m) return <p className="text-white/60">Loading metrics…</p>;
  const dollars = (c) => `$${(c/100).toLocaleString(undefined,{minimumFractionDigits:0,maximumFractionDigits:2})}`;
  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <section>
        <p className="text-[10px] tracking-widest uppercase text-[#D4AF37] mb-3">Revenue</p>
        <div className="grid md:grid-cols-3 gap-4">
          <Metric icon={<DollarSign className="w-5 h-5"/>} label="All Time" value={dollars(m.revenue.all_time_cents)} sub={`${m.revenue.all_time_txn} transactions`} testid="metric-revenue-all"/>
          <Metric icon={<TrendingUp className="w-5 h-5"/>} label="Last 30 Days" value={dollars(m.revenue.last_30d_cents)} sub={`${m.revenue.last_30d_txn} transactions`} testid="metric-revenue-30d"/>
          <Metric icon={<TrendingUp className="w-5 h-5"/>} label="Last 7 Days" value={dollars(m.revenue.last_7d_cents)} sub={`${m.revenue.last_7d_txn} transactions`} testid="metric-revenue-7d"/>
        </div>
      </section>
      <section>
        <p className="text-[10px] tracking-widest uppercase text-[#D4AF37] mb-3">Users</p>
        <div className="grid md:grid-cols-3 gap-4">
          <Metric icon={<UsersIcon className="w-5 h-5"/>} label="Total Users" value={m.users.total.toLocaleString()} testid="metric-users-total"/>
          <Metric icon={<UsersIcon className="w-5 h-5"/>} label="Premium Subscribers" value={m.users.premium.toLocaleString()} testid="metric-users-premium"/>
          <Metric icon={<Radio className="w-5 h-5"/>} label="Channel Owners" value={m.users.channel_owners.toLocaleString()} testid="metric-channel-owners"/>
        </div>
      </section>
      <section>
        <p className="text-[10px] tracking-widest uppercase text-[#D4AF37] mb-3">Advertising</p>
        <div className="grid md:grid-cols-4 gap-4">
          <Metric icon={<Megaphone className="w-5 h-5"/>} label="Active Ads" value={m.ads.active} testid="metric-ads-active"/>
          <Metric icon={<Megaphone className="w-5 h-5"/>} label="Pending Review" value={m.ads.pending} testid="metric-ads-pending"/>
          <Metric icon={<Eye className="w-5 h-5"/>} label="Total Impressions" value={m.ads.impressions.toLocaleString()} testid="metric-ads-impressions"/>
          <Metric icon={<MousePointerClick className="w-5 h-5"/>} label="Total Clicks" value={m.ads.clicks.toLocaleString()} sub={m.ads.impressions ? `${((m.ads.clicks/m.ads.impressions)*100).toFixed(2)}% CTR` : ""} testid="metric-ads-clicks"/>
        </div>
      </section>
      <section>
        <p className="text-[10px] tracking-widest uppercase text-[#D4AF37] mb-3">Content & Gifts</p>
        <div className="grid md:grid-cols-4 gap-4">
          <Metric icon={<Radio className="w-5 h-5"/>} label="Channels" value={m.content.channels_active} sub={`${m.content.channels_total} total`} testid="metric-channels"/>
          <Metric icon={<Newspaper className="w-5 h-5"/>} label="News Items" value={m.content.news_items.toLocaleString()} testid="metric-news"/>
          <Metric icon={<Gift className="w-5 h-5"/>} label="Gift Codes Sold" value={m.gifts.paid_codes.toLocaleString()} sub={`${m.gifts.redeemed_codes} redeemed`} testid="metric-gifts"/>
          <Metric icon={<UsersIcon className="w-5 h-5"/>} label="Bulk Bundles" value={m.gifts.bulk_bundles_paid.toLocaleString()} testid="metric-bundles"/>
        </div>
      </section>
      <WeeklyDigest />
    </div>
  );
}

function WeeklyDigest() {
  const [preview, setPreview] = useState(null);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const [p, l] = await Promise.all([api.get("/admin/digest/preview"), api.get("/admin/digest/logs")]);
      setPreview(p.data); setLogs(l.data);
    } catch (e) { toast.error("Failed to load digest"); }
  };
  useEffect(() => { load(); }, []);
  const sendNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/digest/send-now");
      toast.success(`Digest queued (${r.data.delivery_status}) to ${r.data.recipient}`);
      await load();
    } catch (e) { toast.error("Send failed"); }
    setBusy(false);
  };
  if (!preview) return null;
  return (
    <section data-testid="weekly-digest-section">
      <div className="flex items-end justify-between mb-3">
        <p className="text-[10px] tracking-widest uppercase text-[#D4AF37]">Weekly Operator's Digest</p>
        <button onClick={sendNow} disabled={busy} data-testid="digest-send-now-btn"
          className="text-xs font-semibold px-3 py-1.5 rounded-md bg-[#D4AF37] text-[#050507] hover:bg-[#E5C15B] disabled:opacity-50 transition">
          {busy ? "Sending…" : "Send Digest Now"}
        </button>
      </div>
      <div className="rounded-xl border border-white/10 bg-[#0A0A0E] p-5 grid md:grid-cols-2 gap-5">
        <div>
          <p className="text-white font-heading text-lg mb-1">{preview.subject}</p>
          <p className="text-xs text-white/50 mb-3">To: <span className="text-white/80">{preview.recipient}</span> · Delivery: <span className="text-[#D4AF37]">mocked (log-only)</span></p>
          <div className="text-sm text-white/70 space-y-1">
            <p>New users (7d): <span className="text-white font-semibold">{preview.metrics.users.new_7d}</span></p>
            <p>New premium (7d): <span className="text-white font-semibold">{preview.metrics.users.new_premium_7d}</span></p>
            <p>Revenue (7d): <span className="text-white font-semibold">${preview.metrics.revenue_7d.usd.toLocaleString()}</span> · {preview.metrics.revenue_7d.transactions} txn</p>
            <p>Gift codes (7d): <span className="text-white font-semibold">{preview.metrics.gifts_7d.gift_codes}</span> · Bundles: <span className="text-white font-semibold">{preview.metrics.gifts_7d.bulk_bundles}</span></p>
          </div>
          <p className="text-[11px] text-white/40 mt-4">Auto-fires Mondays 09:00 UTC. Wire an email provider in <code>backend/server.py::_send_digest</code> for real delivery.</p>
        </div>
        <div>
          <p className="text-[10px] tracking-widest uppercase text-white/50 mb-2">Recent Digest Log</p>
          <div className="border border-white/10 rounded-lg divide-y divide-white/5 max-h-56 overflow-y-auto">
            {logs.length === 0 && <p className="p-3 text-xs text-white/40">No digests sent yet.</p>}
            {logs.map(l => (
              <div key={l.id} className="p-3 text-xs" data-testid={`digest-log-${l.id}`}>
                <p className="text-white/90 font-medium">{l.subject}</p>
                <p className="text-white/50 mt-0.5">{new Date(l.sent_at).toLocaleString()} · {l.recipient} · <span className="text-[#D4AF37]">{l.delivery_status}</span></p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Metric({ icon, label, value, sub, testid }) {
  return (
    <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-[#D4AF37] mb-3">{icon}<span className="text-[10px] font-semibold tracking-widest uppercase">{label}</span></div>
      <p className="font-heading text-3xl font-bold text-white">{value}</p>
      {sub && <p className="text-xs text-white/50 mt-1">{sub}</p>}
    </div>
  );
}

function Tab({ to, label }) {
  return <NavLink to={to} className={({isActive}) => `px-4 py-2 rounded-full text-sm ${isActive ? "bg-[#D4AF37] text-[#050507] font-semibold" : "bg-white/5 border border-white/10 text-white/70 hover:bg-white/10"}`} data-testid={`admin-tab-${to.split("/").pop()}`}>{label}</NavLink>;
}

function Channels() {
  const [channels, setChannels] = useState([]);
  const [newCh, setNewCh] = useState({ slug: "", name: "", tagline: "", logo_url: "", stream_url: "", stream_type: "hls", color: "#D4AF37" });
  const [newProg, setNewProg] = useState({}); // per channel

  const load = async () => setChannels(await adminListChannels());
  useEffect(() => { load(); }, []);

  const createCh = async () => {
    try { await adminCreateChannel(newCh); toast.success("Channel created"); setNewCh({ slug: "", name: "", tagline: "", logo_url: "", stream_url: "", stream_type: "hls", color: "#D4AF37" }); load(); }
    catch (e) { toast.error("Failed"); }
  };
  const removeCh = async (slug) => { if (!window.confirm("Delete channel?")) return; await adminDeleteChannel(slug); toast.success("Deleted"); load(); };
  const addProg = async (slug) => {
    const p = newProg[slug] || {};
    if (!p.title || !p.start_time || !p.duration_min) return toast.error("Fill title, start, duration");
    await adminAddProgram(slug, { title: p.title, description: p.description || "", start_time: p.start_time, duration_min: Number(p.duration_min), host: p.host || null });
    toast.success("Program added"); setNewProg({ ...newProg, [slug]: {} }); load();
  };
  const rmProg = async (slug, pid) => { await adminRemoveProgram(slug, pid); toast.success("Removed"); load(); };

  return (
    <div className="space-y-8" data-testid="admin-channels">
      <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-6">
        <h3 className="font-heading text-xl mb-4">Add New Channel</h3>
        <div className="grid md:grid-cols-3 gap-3">
          {[["slug","Slug (e.g., vot-kids)"],["name","Channel Name"],["tagline","Tagline"],["logo_url","Logo URL"],["stream_url","Stream URL (.m3u8 or .mp4)"],["color","Accent Color (hex)"]].map(([k,ph]) => (
            <Input key={k} placeholder={ph} value={newCh[k]} onChange={v => setNewCh({...newCh, [k]:v})} testid={`new-channel-${k}`} />
          ))}
          <select value={newCh.stream_type} onChange={e=>setNewCh({...newCh, stream_type: e.target.value})} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="new-channel-type">
            <option value="hls">HLS (.m3u8)</option><option value="mp4">MP4</option>
          </select>
        </div>
        <button onClick={createCh} className="mt-4 btn-gold px-5 py-2 rounded-full text-sm flex items-center gap-2" data-testid="create-channel-btn"><Plus className="w-4 h-4"/>Create Channel</button>
      </div>

      {channels.map(ch => (
        <div key={ch.slug} className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-6" data-testid={`channel-panel-${ch.slug}`}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-heading text-xl">{ch.name} <span className="text-xs text-white/50">{ch.slug}</span></h3>
              <p className="text-sm text-white/60">{ch.tagline}</p>
              <p className="text-xs text-white/40 mt-1">{ch.stream_type?.toUpperCase()} · {ch.stream_url?.slice(0,80)}…</p>
            </div>
            <button onClick={() => removeCh(ch.slug)} className="text-white/50 hover:text-[#E11D48] p-2" data-testid={`delete-channel-${ch.slug}`}><Trash2 className="w-4 h-4"/></button>
          </div>
          <p className="text-xs uppercase tracking-widest text-white/40 mb-2">Schedule</p>
          <div className="space-y-1 mb-4 max-h-60 overflow-y-auto">
            {(ch.schedule || []).map(p => (
              <div key={p.id} className="flex items-center justify-between text-sm bg-white/[0.03] px-3 py-2 rounded">
                <span><span className="text-white/50">{new Date(p.start_time).toLocaleString([], {month:'short',day:'numeric',hour:'numeric',minute:'2-digit'})}</span> · <span className="font-medium">{p.title}</span> · <span className="text-white/50">{p.duration_min}m</span></span>
                <button onClick={() => rmProg(ch.slug, p.id)} className="text-white/40 hover:text-[#E11D48]" data-testid={`remove-prog-${p.id}`}><Trash2 className="w-3.5 h-3.5"/></button>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-5 gap-2">
            <Input placeholder="Program title" value={newProg[ch.slug]?.title||""} onChange={v=>setNewProg({...newProg, [ch.slug]:{...(newProg[ch.slug]||{}), title:v}})} testid={`prog-title-${ch.slug}`} />
            <Input placeholder="Description" value={newProg[ch.slug]?.description||""} onChange={v=>setNewProg({...newProg, [ch.slug]:{...(newProg[ch.slug]||{}), description:v}})} testid={`prog-desc-${ch.slug}`} />
            <Input placeholder="Start ISO (2026-02-19T14:00)" value={newProg[ch.slug]?.start_time||""} onChange={v=>setNewProg({...newProg, [ch.slug]:{...(newProg[ch.slug]||{}), start_time:v}})} testid={`prog-start-${ch.slug}`} />
            <Input placeholder="Duration (min)" value={newProg[ch.slug]?.duration_min||""} onChange={v=>setNewProg({...newProg, [ch.slug]:{...(newProg[ch.slug]||{}), duration_min:v}})} testid={`prog-dur-${ch.slug}`} />
            <button onClick={() => addProg(ch.slug)} className="btn-gold rounded-lg text-sm px-3 py-2" data-testid={`add-prog-${ch.slug}`}>+ Add</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Library() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", thumbnail: "", poster: "", duration_min: 30, category: "Sunday Sermons", year: 2026, rating: "TV-G", video_url: "", stream_type: "mp4", channel_id: "" });
  const load = async () => setItems(await fetchLibrary());
  useEffect(() => { load(); }, []);

  const add = async () => {
    try { await adminCreateContent({ ...form, duration_min: Number(form.duration_min), year: Number(form.year) }); toast.success("Added"); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => { if(!window.confirm("Delete?")) return; await adminDeleteContent(id); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-6" data-testid="admin-library">
      <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-6">
        <h3 className="font-heading text-xl mb-4">Add New Content</h3>
        <div className="grid md:grid-cols-3 gap-3">
          {[["title","Title"],["description","Description"],["thumbnail","Thumbnail URL"],["poster","Poster URL"],["duration_min","Duration (min)"],["category","Category"],["year","Year"],["rating","Rating"],["video_url","Video URL"],["channel_id","Channel ID (optional)"]].map(([k,ph]) => (
            <Input key={k} placeholder={ph} value={form[k]} onChange={v=>setForm({...form,[k]:v})} testid={`content-${k}`} />
          ))}
          <select value={form.stream_type} onChange={e=>setForm({...form, stream_type:e.target.value})} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm"><option value="mp4">MP4</option><option value="hls">HLS</option></select>
        </div>
        <button onClick={add} className="mt-4 btn-gold px-5 py-2 rounded-full text-sm flex items-center gap-2" data-testid="add-content-btn"><Plus className="w-4 h-4"/>Add Content</button>
      </div>
      <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-6">
        <h3 className="font-heading text-xl mb-4">All Content ({items.length})</h3>
        <div className="space-y-1 max-h-[600px] overflow-y-auto">
          {items.map(it => (
            <div key={it.id} className="flex items-center justify-between text-sm bg-white/[0.03] px-3 py-2 rounded" data-testid={`lib-item-${it.id}`}>
              <div><span className="font-medium">{it.title}</span> <span className="text-white/50">· {it.category} · {it.duration_min}m</span></div>
              <button onClick={() => remove(it.id)} className="text-white/40 hover:text-[#E11D48]"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Users() {
  const [users, setUsers] = useState([]);
  useEffect(() => { adminListUsers().then(setUsers); }, []);
  return (
    <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-6" data-testid="admin-users">
      <h3 className="font-heading text-xl mb-4">Users ({users.length})</h3>
      <div className="space-y-1">
        {users.map(u => (
          <div key={u.user_id} className="flex items-center justify-between text-sm bg-white/[0.03] px-3 py-2 rounded">
            <div><span className="font-medium">{u.name || u.email}</span> <span className="text-white/50">· {u.email}</span></div>
            <span className={`text-xs px-2 py-0.5 rounded ${u.role === "admin" ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "bg-white/10 text-white/60"}`}>{u.role || "user"}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Input({ testid, ...props }) {
  return <input {...props} onChange={e=>props.onChange(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60 w-full" data-testid={testid} />;
}

function Ads() {
  const [ads, setAds] = useState([]);
  const [filter, setFilter] = useState("");
  const load = async () => setAds(await adminListAds(filter || undefined));
  useEffect(() => { load(); }, [filter]);

  const approve = async (id) => { await adminApproveAd(id); toast.success("Approved"); load(); };
  const reject = async (id) => { await adminRejectAd(id); toast.success("Rejected"); load(); };
  const del = async (id) => { if (!window.confirm("Delete ad?")) return; await adminDeleteAd(id); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-4" data-testid="admin-ads">
      <div className="flex gap-2">
        {["", "pending_review", "active", "rejected", "draft"].map(f => (
          <button key={f} onClick={()=>setFilter(f)} className={`px-3 py-1 rounded-full text-xs ${filter===f ? "bg-[#D4AF37] text-black font-semibold" : "bg-white/5 text-white/60 border border-white/10"}`} data-testid={`filter-${f || "all"}`}>{f || "All"}</button>
        ))}
      </div>
      {ads.length === 0 ? <p className="text-white/60 text-sm">No ads.</p> : ads.map(ad => (
        <div key={ad.id} className="bg-[#0A0A0E] border border-white/10 rounded-xl p-4 flex gap-4" data-testid={`admin-ad-${ad.id}`}>
          <img src={ad.image_url} alt="" className="w-32 h-20 object-cover rounded-lg border border-white/10 flex-shrink-0" onError={e=>e.target.style.display='none'} />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="font-heading font-semibold text-white">{ad.title}</p>
                <p className="text-xs text-white/50 mt-1">{ad.advertiser_email} · {ad.slot}{ad.channel_id ? ` · ${ad.channel_id}` : ""}</p>
                <p className="text-xs text-white/40 mt-1">Status: <span className="text-white/70">{ad.status}</span> · Paid: <span className="text-white/70">{ad.payment_status}</span> · ${ad.price_usd?.toFixed(0)}</p>
                <a href={ad.click_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#D4AF37] hover:underline">{ad.click_url}</a>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {ad.status === "pending_review" && ad.payment_status === "paid" && <button onClick={()=>approve(ad.id)} className="btn-gold rounded-full px-3 py-1.5 text-xs flex items-center gap-1" data-testid={`approve-${ad.id}`}><Check className="w-3 h-3"/>Approve</button>}
                {ad.status !== "rejected" && <button onClick={()=>reject(ad.id)} className="btn-ghost rounded-full px-3 py-1.5 text-xs flex items-center gap-1" data-testid={`reject-${ad.id}`}><X className="w-3 h-3"/>Reject</button>}
                <button onClick={()=>del(ad.id)} className="text-white/40 hover:text-[#E11D48]"><Trash2 className="w-4 h-4"/></button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function News() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", image_url: "", author: "" });
  const load = async () => setItems(await adminListNews());
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.title || !form.body) return toast.error("Title and body required");
    try { await adminCreateNews(form); toast.success("Published"); setForm({title:"",body:"",image_url:"",author:""}); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete news?")) return; await adminDeleteNews(id); toast.success("Deleted"); load(); };
  const refresh = async () => { const r = await adminRefreshRss(); toast.success(`Added ${r.added} RSS items`); load(); };

  return (
    <div className="space-y-6" data-testid="admin-news">
      <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4"><h3 className="font-heading text-xl">Publish News Post</h3>
          <button onClick={refresh} className="btn-ghost rounded-full px-4 py-2 text-sm flex items-center gap-2" data-testid="rss-refresh"><RefreshCw className="w-4 h-4"/>Refresh RSS</button>
        </div>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <Input placeholder="Title" value={form.title} onChange={v=>setForm({...form,title:v})} testid="news-title"/>
          <Input placeholder="Author (optional)" value={form.author} onChange={v=>setForm({...form,author:v})} testid="news-author"/>
          <Input placeholder="Image URL (optional)" value={form.image_url} onChange={v=>setForm({...form,image_url:v})} testid="news-image"/>
        </div>
        <textarea rows={5} placeholder="Body (HTML allowed)" value={form.body} onChange={e=>setForm({...form,body:e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/60" data-testid="news-body"/>
        <button onClick={create} className="mt-3 btn-gold px-5 py-2 rounded-full text-sm flex items-center gap-2" data-testid="publish-news"><Plus className="w-4 h-4"/>Publish</button>
      </div>
      <div className="bg-[#0A0A0E] border border-white/10 rounded-2xl p-6">
        <h3 className="font-heading text-xl mb-4">All News ({items.length})</h3>
        <div className="space-y-1 max-h-[600px] overflow-y-auto">
          {items.map(n => (
            <div key={n.id} className="flex items-center justify-between text-sm bg-white/[0.03] px-3 py-2 rounded gap-3" data-testid={`admin-news-${n.id}`}>
              <div className="min-w-0 flex-1"><span className="font-medium truncate">{n.title}</span> <span className="text-white/50 text-xs">· {n.source} · {new Date(n.published_at).toLocaleDateString()}</span></div>
              <button onClick={()=>del(n.id)} className="text-white/40 hover:text-[#E11D48] flex-shrink-0"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
